import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DollarSign, TrendingDown, Zap, Leaf, AlertTriangle, ShieldAlert, Bell, Cloud, Server, Database, Activity, HardDrive, RefreshCw, Battery, TreePine, Target, TrendingUp } from 'lucide-react';
import { costApi, optimizationApi, awsApi } from '../services/api';
import { StatCard, Card, SectionHeader, Loader, Button } from '../components/ui';
import { useRole } from '../hooks/useRole';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const AWS_COLORS = ['#ff9900', '#ffb800', '#4a9eff', '#00c896', '#a78bfa', '#fb923c', '#f472b6', '#34d399'];

const COLORS = ['#0084ff', '#00c896', '#ffb800', '#ff5c5c', '#a78bfa', '#fb923c', '#34d399'];

const FALLBACK_TREND = [
  { month: 'Jul', spend: 920000, savings: 85000 },
  { month: 'Aug', spend: 980000, savings: 102000 },
  { month: 'Sep', spend: 1050000, savings: 145000 },
  { month: 'Oct', spend: 1020000, savings: 189000 },
  { month: 'Nov', spend: 990000, savings: 234000 },
  { month: 'Dec', spend: 960000, savings: 287000 },
];

function fmt(n) {
  if (n === null || n === undefined || n === 0) return 'No data';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${Number(n).toFixed(2)}`;
}

const tooltipStyle = {
  background: '#0d1527',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

export default function DashboardPage() {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const { isEndUser } = useRole();

  const handleRefreshBilling = async () => {
    setRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    await queryClient.invalidateQueries({ queryKey: ['aws-current-month'] });
    await queryClient.invalidateQueries({ queryKey: ['aws-forecast'] });
    await queryClient.invalidateQueries({ queryKey: ['aws-daily-trend'] });
    await queryClient.invalidateQueries({ queryKey: ['aws-by-service'] });
    await queryClient.invalidateQueries({ queryKey: ['aws-usage-breakdown'] });
    await queryClient.invalidateQueries({ queryKey: ['aws-service-usage'] });
    await queryClient.invalidateQueries({ queryKey: ['opt-summary'] });
    setTimeout(() => setRefreshing(false), 1500);
  };

  const { data: dashData, isLoading: loadingDash } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => costApi.getDashboard().then((r) => r.data),
    refetchInterval: 30000,
  });

  const { data: optData, isLoading: loadingOpt } = useQuery({
    queryKey: ['opt-summary'],
    queryFn: () => optimizationApi.getSummary().then((r) => r.data),
    refetchInterval: 30000,
  });

  const { data: awsCurrent } = useQuery({
    queryKey: ['aws-current-month'],
    queryFn: () => awsApi.getCurrentMonth().then((r) => r.data),
    refetchInterval: 60000,
  });

  const { data: awsForecast } = useQuery({
    queryKey: ['aws-forecast'],
    queryFn: () => awsApi.getForecast().then((r) => r.data),
    refetchInterval: 60000,
  });

  const { data: awsDaily } = useQuery({
    queryKey: ['aws-daily-trend'],
    queryFn: () => awsApi.getDailyTrend().then((r) => r.data),
    refetchInterval: 60000,
  });

  const { data: awsServices } = useQuery({
    queryKey: ['aws-by-service'],
    queryFn: () => awsApi.getCostByService(1).then((r) => r.data),
    refetchInterval: 60000,
  });

  const { data: awsUsage } = useQuery({
    queryKey: ['aws-usage-breakdown'],
    queryFn: () => awsApi.getUsageBreakdown().then((r) => r.data),
    refetchInterval: 60000,
  });

  const { data: awsServiceUsage } = useQuery({
    queryKey: ['aws-service-usage'],
    queryFn: () => awsApi.getServiceUsage().then((r) => r.data),
    refetchInterval: 60000,
  });

  const { data: susData } = useQuery({
    queryKey: ['sustainability'],
    queryFn: () => costApi.getSustainability().then((r) => r.data),
    refetchInterval: 60000,
  });

  const { data: liveAnalysis } = useQuery({
    queryKey: ['live-analysis-dashboard'],
    queryFn: () => import('../services/api').then(m => m.optimizationApi.getLiveAnalysis()).then((r) => r.data),
    refetchInterval: 60000,
  });

  // Don't block on DB data — show loader only if no AWS data yet
  const isLoading = !awsCurrent && !liveAnalysis && loadingDash;
  if (isLoading) return <Loader />;

  // ── ALL KPIs derived from LIVE AWS data (tenant-aware) ──
  const awsCurrentCost = awsCurrent?.blendedCost ? parseFloat(awsCurrent.blendedCost) : 0;
  const awsForecastCost = awsForecast?.forecast ? parseFloat(awsForecast.forecast) : 0;
  const currentSpend = awsCurrentCost;

  // Use previous full months from service data for annual projection and trend
  const allServiceMonths = awsServices?.months || [];
  // Last month (most recent complete month)
  const prevMonthServices = allServiceMonths.length >= 2 ? allServiceMonths[allServiceMonths.length - 2] : null;
  const previousSpend = prevMonthServices
    ? prevMonthServices.services.reduce((s, sv) => s + parseFloat(sv.cost || 0), 0)
    : awsForecastCost > 0 ? awsForecastCost : 0;
  // Month before last (for trend comparison)
  const twoMonthsAgoServices = allServiceMonths.length >= 3 ? allServiceMonths[allServiceMonths.length - 3] : null;
  const twoMonthsAgoSpend = twoMonthsAgoServices
    ? twoMonthsAgoServices.services.reduce((s, sv) => s + parseFloat(sv.cost || 0), 0)
    : 0;
  // Month-over-month change
  const momChange = twoMonthsAgoSpend > 0 ? ((previousSpend - twoMonthsAgoSpend) / twoMonthsAgoSpend * 100) : null;

  const liveSavings = liveAnalysis?.totalPotentialSavings ? parseFloat(liveAnalysis.totalPotentialSavings) : 0;
  const liveRulesTriggered = liveAnalysis?.triggeredRules || 0;
  const liveTotalRules = liveAnalysis?.totalRules || 0;

  // Build spend-by-service breakdown from live AWS data
  const serviceData = awsServices?.months?.[0]?.services || [];
  const pieData = serviceData.slice(0, 7).map((s) => ({
    name: s.service.replace('Amazon ', '').replace('AWS ', '').substring(0, 20),
    value: parseFloat(s.cost),
  }));

  // Use live analysis dimensions for opt chart
  const optByDim = liveAnalysis?.recommendations
    ? Object.entries(
        liveAnalysis.recommendations.reduce((acc, r) => {
          acc[r.dimension] = (acc[r.dimension] || 0) + 1;
          return acc;
        }, {})
      ).map(([name, count]) => ({ name, count }))
    : null;

  const trendData = dashData?.monthlySavingsTrend || FALLBACK_TREND;

  return (
    <div>
      {isEndUser && (
        <Card style={{
          padding: 18,
          marginBottom: 20,
          background: 'linear-gradient(135deg, rgba(0,132,255,0.10), rgba(0,200,150,0.08))',
          border: '1px solid rgba(0,132,255,0.25)',
        }}>
          <div style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
            Potential additional savings: {fmt(liveSavings || 0)}/month
          </div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, lineHeight: 1.6 }}>
            SmalBlu has identified opportunities to reduce your cloud spend.
            Contact your SmalBlu advisor to review the details and approve changes.
          </div>
        </Card>
      )}
      <SectionHeader
        title="Infrastructure Overview"
        subtitle="Real-time cost, performance, and sustainability metrics (auto-refreshes every 30s)"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="primary" size="sm" onClick={handleRefreshBilling} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              opacity: refreshing ? 0.7 : 1, pointerEvents: refreshing ? 'none' : 'auto',
            }}>
              <RefreshCw size={14} style={{
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
              }} />
              {refreshing ? 'Refreshing...' : 'Refresh Billing'}
            </Button>
            <Button variant="secondary" size="sm">Export Report</Button>
          </div>
        }
      />

      {/* KPI Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 16,
        marginBottom: 24,
      }}>
        <StatCard
          label="Annual Cloud Spend"
          value={previousSpend > 0 ? fmt(previousSpend * 12) : currentSpend > 0 ? fmt(currentSpend * 12) : 'No data'}
          subValue={previousSpend > 0
            ? `Last month: ${fmt(previousSpend)}${twoMonthsAgoSpend > 0 ? ` · Prior: ${fmt(twoMonthsAgoSpend)}` : ''}`
            : currentSpend > 0 ? `MTD: ${fmt(currentSpend)}` : 'Connect AWS credentials'}
          trend={momChange}
          icon={DollarSign}
          accentColor="#0084ff"
        />
        <StatCard
          label="Potential Savings (Annual)"
          value={liveSavings > 0 ? fmt(liveSavings * 12) : 'No data'}
          subValue={liveSavings > 0 ? `${liveRulesTriggered} rules · ${fmt(liveSavings)}/mo` : 'Run analysis first'}
          trend={liveSavings > 0 && previousSpend > 0 ? (liveSavings / previousSpend * 100) : null}
          icon={TrendingDown}
          accentColor="#00c896"
        />
        <StatCard
          label="Active Recommendations"
          value={liveRulesTriggered > 0 ? `${liveRulesTriggered} / ${liveTotalRules}` : 'No data'}
          subValue={liveSavings > 0 ? `${fmt(liveSavings * 12)}/yr potential` : 'No rules triggered'}
          icon={Zap}
          accentColor="#ffb800"
        />
        <StatCard
          label="Annual Forecast"
          value={previousSpend > 0 ? fmt((previousSpend - liveSavings) * 12) : 'No data'}
          subValue={previousSpend > 0 ? `Spend ${fmt(previousSpend * 12)} - Savings ${fmt(liveSavings * 12)}` : ''}
          trend={liveSavings > 0 && previousSpend > 0 ? -(liveSavings / previousSpend * 100) : null}
          icon={TrendingDown}
          accentColor="#34d399"
        />
      </div>

      {/* Alert Summary Card */}
      <Card style={{ marginBottom: 24, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10,
              background: 'rgba(255,92,92,0.15)', border: '1px solid rgba(255,92,92,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlertTriangle size={20} color="#ff5c5c" />
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Alert Summary</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginTop: 2 }}>Active alerts requiring attention</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#ff5c5c', fontSize: 24, fontWeight: 700 }}>3</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                <ShieldAlert size={11} /> Critical
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#ffb800', fontSize: 24, fontWeight: 700 }}>5</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                <AlertTriangle size={11} /> Warning
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#4a9eff', fontSize: 24, fontWeight: 700 }}>2</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Bell size={11} /> Info
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Sustainability section moved to /sustainability page */}
      {false && susData && (
        <>
          {/* Energy Savings + Carbon Points + Projected Savings KPI Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            {/* Energy Saved */}
            <Card style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(0,200,150,0.12)', border: '1px solid rgba(0,200,150,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Battery size={18} color="#00c896" />
                </div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 600 }}>Energy Saved</div>
              </div>
              <div style={{ color: '#00c896', fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
                {(susData.energySavedKwh / 1000).toFixed(1)}K <span style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.4)' }}>kWh</span>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 6 }}>
                {susData.energySavedPercent}% reduction from baseline
              </div>
            </Card>

            {/* Carbon Points */}
            <Card style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Target size={18} color="#34d399" />
                </div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 600 }}>Carbon Score</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ color: '#34d399', fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>{susData.carbonPointsEarned}</span>
                <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 13 }}>/ {susData.carbonPointsMax}</span>
                <span style={{
                  marginLeft: 'auto', padding: '3px 10px', borderRadius: 6, fontSize: 13, fontWeight: 700,
                  background: susData.carbonGrade.startsWith('A') ? 'rgba(52,211,153,0.15)' : 'rgba(255,184,0,0.15)',
                  color: susData.carbonGrade.startsWith('A') ? '#34d399' : '#ffb800',
                  border: `1px solid ${susData.carbonGrade.startsWith('A') ? 'rgba(52,211,153,0.3)' : 'rgba(255,184,0,0.3)'}`,
                }}>{susData.carbonGrade}</span>
              </div>
              {/* Progress bar */}
              <div style={{ marginTop: 10, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)' }}>
                <div style={{
                  height: '100%', borderRadius: 3, width: `${(susData.carbonPointsEarned / susData.carbonPointsMax) * 100}%`,
                  background: 'linear-gradient(90deg, #34d399, #00c896)',
                  transition: 'width 0.5s ease',
                }} />
              </div>
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 6 }}>
                {susData.carbonReductionPercent}% carbon reduction achieved
              </div>
            </Card>

            {/* Trees Equivalent */}
            <Card style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(74,158,255,0.12)', border: '1px solid rgba(74,158,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TreePine size={18} color="#4a9eff" />
                </div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 600 }}>CO2 Impact</div>
              </div>
              <div style={{ color: '#4a9eff', fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
                {susData.treesEquivalent} <span style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.4)' }}>trees</span>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 6 }}>
                Equivalent CO2 absorbed annually
              </div>
            </Card>

            {/* Projected Savings */}
            <Card style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TrendingUp size={18} color="#a78bfa" />
                </div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 600 }}>Projected Savings</div>
              </div>
              <div style={{ color: '#a78bfa', fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
                {fmt(susData.projectedAnnualSavings)}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 6 }}>
                Annual projection ({fmt(susData.projectedMonthlySavings)}/mo)
              </div>
            </Card>
          </div>

          {/* Charts: Carbon Trend + Energy Breakdown + Projected Savings */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
            {/* Carbon Trend & Points */}
            <Card>
              <div style={{ color: '#fff', fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Carbon Footprint Trend</div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={susData.carbonTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line yAxisId="left" type="monotone" dataKey="carbonKg" stroke="#ff5c5c" strokeWidth={2} dot={{ r: 3 }} name="CO2 (kg)" />
                  <Line yAxisId="right" type="monotone" dataKey="carbonPoints" stroke="#34d399" strokeWidth={2} dot={{ r: 3 }} name="Carbon Points" />
                  <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            {/* Energy Saving Breakdown */}
            <Card>
              <div style={{ color: '#fff', fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Energy Saving Breakdown</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {susData.energyBreakdown?.map((item, i) => {
                  const colors = ['#00c896', '#4a9eff', '#ffb800', '#a78bfa'];
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{item.category}</span>
                        <span style={{ color: colors[i], fontSize: 12, fontWeight: 600 }}>{(item.savedKwh / 1000).toFixed(1)}K kWh</span>
                      </div>
                      <div style={{ height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.06)' }}>
                        <div style={{
                          height: '100%', borderRadius: 4, width: `${item.percentReduction}%`,
                          background: colors[i], transition: 'width 0.5s ease',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 16, padding: '10px 12px', borderRadius: 8, background: 'rgba(0,200,150,0.06)', border: '1px solid rgba(0,200,150,0.12)', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Total Saved</span>
                <span style={{ color: '#00c896', fontSize: 13, fontWeight: 700 }}>{(susData.energySavedKwh / 1000).toFixed(1)}K kWh/mo</span>
              </div>
            </Card>

            {/* Projected Savings Trend */}
            <Card>
              <div style={{ color: '#fff', fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Projected Savings (6mo)</div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={susData.projectedSavingsTrend?.map(d => ({ month: d.period, savings: Number(d.savings), spend: Number(d.spend) }))}>
                  <defs>
                    <linearGradient id="gradProj" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#a78bfa" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmt(v)]} />
                  <Area type="monotone" dataKey="savings" stroke="#a78bfa" strokeWidth={2} fill="url(#gradProj)" name="Projected Savings" />
                  <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </>
      )}

      {/* AWS Billing - Real Data */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'rgba(255,153,0,0.15)', border: '1px solid rgba(255,153,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Cloud size={18} color="#ff9900" />
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>AWS Billing (Live)</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>Real-time data from AWS Cost Explorer</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 16, border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 500, marginBottom: 6 }}>Current Month (MTD)</div>
            <div style={{ color: '#ff9900', fontSize: 22, fontWeight: 700 }}>
              ${awsCurrent?.blendedCost ? Number(awsCurrent.blendedCost).toFixed(2) : '—'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 4 }}>
              {awsCurrent?.period || '—'} {awsCurrent?.estimated && '(estimated)'}
            </div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 16, border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 500, marginBottom: 6 }}>Forecasted Total</div>
            <div style={{ color: '#ffb800', fontSize: 22, fontWeight: 700 }}>
              ${awsForecast?.forecastedTotal ? Number(awsForecast.forecastedTotal).toFixed(2) : '—'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 4 }}>
              {awsForecast?.forecastPeriod || 'End of month estimate'}
            </div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 16, border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 500, marginBottom: 6 }}>Usage Quantity</div>
            <div style={{ color: '#4a9eff', fontSize: 22, fontWeight: 700 }}>
              {awsCurrent?.usageQuantity ? Number(awsCurrent.usageQuantity).toFixed(0) : '—'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 4 }}>
              Total usage units this month
            </div>
          </div>
        </div>

        {/* Daily cost trend + Service breakdown side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
          {/* Daily Trend Chart */}
          <div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Daily Cost Trend (Current Month)
            </div>
            {awsDaily?.dailyCosts?.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={awsDaily.dailyCosts.map(d => ({
                  date: d.date.slice(5),
                  cost: Number(d.cost),
                }))}>
                  <defs>
                    <linearGradient id="gradAws" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ff9900" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ff9900" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Cost']} />
                  <Area type="monotone" dataKey="cost" stroke="#ff9900" strokeWidth={2} fill="url(#gradAws)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.2)', fontSize: 13 }}>
                Loading daily trend...
              </div>
            )}
          </div>

          {/* Service Breakdown */}
          <div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Cost by Service
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {(() => {
                const months = awsServices?.months || [];
                const latest = months[months.length - 1];
                const services = (latest?.services || []).filter(s => Number(s.cost) > 0);
                if (services.length === 0) return (
                  <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
                    No service costs yet
                  </div>
                );
                return services.map((s, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
                  }}>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, maxWidth: '65%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.service.replace('Amazon ', '').replace('AWS ', '')}
                    </span>
                    <span style={{ color: '#ff9900', fontSize: 13, fontWeight: 600 }}>
                      ${Number(s.cost).toFixed(2)}
                    </span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      </Card>

      {/* AWS Compute & Usage Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {/* Usage by Category (Compute, Storage, Data Transfer, etc.) */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <Activity size={16} color="#a78bfa" />
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Usage by Category</div>
          </div>
          {(() => {
            const cats = awsUsage?.categories || {};
            const entries = Object.entries(cats).filter(([, v]) => v > 0);
            const total = entries.reduce((s, [, v]) => s + v, 0);
            const catIcons = { Compute: Server, Storage: HardDrive, 'Data Transfer': Activity, Requests: Zap, Other: Cloud };
            const catColors = { Compute: '#ff9900', Storage: '#4a9eff', 'Data Transfer': '#00c896', Requests: '#a78bfa', Other: '#fb923c' };

            if (entries.length === 0 && !awsUsage) {
              return <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 13, textAlign: 'center', padding: 30 }}>Loading usage data...</div>;
            }

            // Show pie chart of categories
            const pieData = entries.map(([name, value]) => ({ name, value }));
            return (
              <>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={catColors[entry.name] || AWS_COLORS[i]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Cost']} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : null}
                <div style={{ marginTop: 8 }}>
                  {Object.entries(cats).map(([cat, cost]) => {
                    const CatIcon = catIcons[cat] || Cloud;
                    const pct = total > 0 ? (cost / total * 100) : 0;
                    return (
                      <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <CatIcon size={14} color={catColors[cat] || '#888'} />
                        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, flex: 1 }}>{cat}</span>
                        <span style={{ color: '#fff', fontSize: 12, fontWeight: 600, width: 80, textAlign: 'right' }}>
                          ${cost.toFixed(2)}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, width: 45, textAlign: 'right' }}>
                          {pct > 0 ? `${pct.toFixed(0)}%` : '0%'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </Card>

        {/* Service Usage Details (cost + usage quantity per service) */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <Server size={16} color="#4a9eff" />
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Service Usage Details</div>
          </div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginBottom: 12 }}>
            {awsServiceUsage?.period || 'Current month'}
          </div>
          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {(() => {
              const services = awsServiceUsage?.services || [];
              if (services.length === 0) {
                return <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 13, textAlign: 'center', padding: 30 }}>Loading...</div>;
              }
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Service', 'Cost (USD)', 'Usage'].map(h => (
                        <th key={h} style={{
                          textAlign: 'left', padding: '8px 8px', color: 'rgba(255,255,255,0.35)', fontSize: 10,
                          fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                          borderBottom: '1px solid rgba(255,255,255,0.06)',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((s, i) => {
                      const cost = Number(s.cost);
                      const usage = Number(s.usage);
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '9px 8px', color: 'rgba(255,255,255,0.7)', fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.service.replace('Amazon ', '').replace('AWS ', '')}
                          </td>
                          <td style={{ padding: '9px 8px', fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: cost > 0 ? '#ff9900' : 'rgba(255,255,255,0.25)' }}>
                            ${cost.toFixed(2)}
                          </td>
                          <td style={{ padding: '9px 8px', fontFamily: 'monospace', fontSize: 11, color: usage > 0 ? '#4a9eff' : 'rgba(255,255,255,0.25)' }}>
                            {usage > 1000 ? `${(usage / 1000).toFixed(1)}K` : usage > 0 ? usage.toFixed(2) : '0'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
          {awsServiceUsage?.totalCost && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(255,153,0,0.06)', borderRadius: 8, border: '1px solid rgba(255,153,0,0.12)', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Total</span>
              <span style={{ color: '#ff9900', fontSize: 13, fontWeight: 700 }}>${Number(awsServiceUsage.totalCost).toFixed(2)} | {Number(awsServiceUsage.totalUsage).toFixed(0)} units</span>
            </div>
          )}
        </Card>
      </div>

      {/* AWS Usage Types Breakdown */}
      {awsUsage?.usageTypes?.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <HardDrive size={16} color="#00c896" />
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Detailed Usage Types</div>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginLeft: 'auto' }}>{awsUsage?.period}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {awsUsage.usageTypes.filter(u => Number(u.cost) > 0 || Number(u.usage) > 0).map((u, i) => {
              const cost = Number(u.cost);
              const usage = Number(u.usage);
              return (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '10px 14px',
                  border: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.usageType}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginTop: 2 }}>
                      {usage > 1000 ? `${(usage / 1000).toFixed(1)}K` : usage.toFixed(2)} units
                    </div>
                  </div>
                  <div style={{ color: cost > 0 ? '#ff9900' : 'rgba(255,255,255,0.25)', fontSize: 13, fontWeight: 700, marginLeft: 12, fontFamily: 'monospace' }}>
                    ${cost.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Charts Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: 16,
        marginBottom: 24,
      }}>
        {/* Spend vs Savings Trend */}
        <Card>
          <div style={{
            color: '#fff',
            fontWeight: 600,
            marginBottom: 20,
            fontSize: 15,
          }}>
            Spend vs Savings Trend
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="gradSpend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0084ff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0084ff" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradSavings" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00c896" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00c896" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="month"
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={{ color: '#fff' }}
                formatter={(v) => [`$${(v / 1000).toFixed(0)}K`]}
              />
              <Area
                type="monotone"
                dataKey="spend"
                stroke="#0084ff"
                strokeWidth={2}
                fill="url(#gradSpend)"
                name="Spend"
              />
              <Area
                type="monotone"
                dataKey="savings"
                stroke="#00c896"
                strokeWidth={2}
                fill="url(#gradSavings)"
                name="Savings"
              />
              <Legend
                wrapperStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Spend Breakdown Pie */}
        <Card>
          <div style={{
            color: '#fff',
            fontWeight: 600,
            marginBottom: 20,
            fontSize: 15,
          }}>
            Spend Breakdown
          </div>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v) => [fmt(v), 'Spend']}
                />
                <Legend
                  wrapperStyle={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 240,
              color: 'rgba(255,255,255,0.2)',
              fontSize: 13,
            }}>
              Connect a cloud provider to see data
            </div>
          )}
        </Card>
      </div>

      {/* Optimization Opportunities by Dimension */}
      {optByDim && (
        <Card>
          <div style={{
            color: '#fff',
            fontWeight: 600,
            marginBottom: 20,
            fontSize: 15,
          }}>
            Optimization Opportunities by Dimension
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={optByDim}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.05)"
              />
              <XAxis
                dataKey="name"
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar
                dataKey="count"
                fill="#0084ff"
                radius={[4, 4, 0, 0]}
                name="Recommendations"
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}
